import {
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
  type HubConnection,
  type IRetryPolicy,
  type RetryContext,
} from '@microsoft/signalr';

/**
 * `idle` – not started (anonymous) · `connecting` – first attempt · `connected` ·
 * `reconnecting` – connection lost or attempts failing, retrying with backoff.
 */
export type RealtimeStatus = 'idle' | 'connecting' | 'connected' | 'reconnecting';

/** Server → client envelope sent through the hub method `event` (ARCHITECTURE §4.8). */
export interface RealtimeEnvelope<TPayload = unknown> {
  type: string;
  payload: TPayload;
}

export type RealtimeHandler<TPayload = unknown> = (
  payload: TPayload,
  envelope: RealtimeEnvelope<TPayload>,
) => void;

/** Client-side event dispatched after a reconnect: events may have been missed, refetch what matters. */
export const REALTIME_RECONNECTED = 'realtime.reconnected';

/**
 * Server event `{ topic }`: the user lost access to a topic (removed from a chat, a group…) and the server removed
 * this connection from it. The manager stops re-subscribing to it; modules may react (close the entity, refetch).
 */
export const REALTIME_TOPIC_REVOKED = 'realtime.topic-revoked';

export const REALTIME_HUB_URL = '/hubs/realtime';
const HEARTBEAT_INTERVAL_MS = 30_000;
const MAX_BACKOFF_MS = 30_000;
/**
 * The hub closes a connection when the access token it was opened with expires (every 15 minutes). The manager opens a
 * fresh connection this long before that (the app refreshes the token a minute ahead) and switches over seamlessly: no
 * offline/online flicker for colleagues and no "reconnected" refetch of everything on screen.
 */
export const RENEW_BEFORE_EXPIRY_MS = 30_000;
const MIN_RENEW_DELAY_MS = 5_000;

/** The `exp` of a JWT in milliseconds, or null when it cannot be read. */
export function tokenExpiryMs(token: string | null | undefined): number | null {
  const payload = token?.split('.')[1];
  if (!payload) return null;
  try {
    const json = atob(payload.replace(/-/gu, '+').replace(/_/gu, '/'));
    const exp = (JSON.parse(json) as { exp?: unknown }).exp;
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
}

/** Exponential backoff with ±20% jitter: 0, ~1s, ~2s, ~4s … capped at 30s. Never gives up. */
export function backoffDelay(attempt: number): number {
  if (attempt <= 0) return 0;
  const base = Math.min(MAX_BACKOFF_MS, 1000 * 2 ** (attempt - 1));
  return Math.round(base * (0.8 + Math.random() * 0.4));
}

function isEnvelope(value: unknown): value is RealtimeEnvelope {
  return (
    typeof value === 'object' && value !== null && typeof (value as { type?: unknown }).type === 'string'
  );
}

class InfiniteBackoffPolicy implements IRetryPolicy {
  nextRetryDelayInMilliseconds(context: RetryContext): number {
    return backoffDelay(context.previousRetryCount);
  }
}

export interface RealtimeManagerOptions {
  /** Supplies a fresh JWT for negotiate / WebSocket (`access_token` query). */
  accessTokenFactory: () => Promise<string | null>;
  url?: string;
  heartbeatIntervalMs?: number;
  /** Test seam: build the underlying connection. */
  connectionFactory?: (
    url: string,
    accessTokenFactory: () => Promise<string>,
    retryPolicy: IRetryPolicy,
  ) => HubConnection;
}

function defaultConnectionFactory(
  url: string,
  accessTokenFactory: () => Promise<string>,
  retryPolicy: IRetryPolicy,
): HubConnection {
  return new HubConnectionBuilder()
    .withUrl(url, { accessTokenFactory })
    .withAutomaticReconnect(retryPolicy)
    .configureLogging(import.meta.env.DEV ? LogLevel.Warning : LogLevel.Error)
    .build();
}

/**
 * One SignalR connection to `/hubs/realtime` shared by the whole SPA.
 * - dispatches `event` envelopes to handlers registered by type (`tasks.task-updated`) or prefix (`tasks.*`, `*`);
 * - ref-counts topic subscriptions (`chat:{id}`) and re-subscribes them after every reconnect;
 * - reconnects forever with exponential backoff (also when the initial start fails);
 * - sends `Heartbeat()` periodically for presence.
 */
export class RealtimeManager {
  private readonly options: Required<Omit<RealtimeManagerOptions, 'connectionFactory'>> &
    Pick<RealtimeManagerOptions, 'connectionFactory'>;
  private connection: HubConnection | null = null;
  private status: RealtimeStatus = 'idle';
  private running = false;
  private hasConnectedOnce = false;
  private restartAttempt = 0;
  private restartTimer: ReturnType<typeof setTimeout> | undefined;
  private renewTimer: ReturnType<typeof setTimeout> | undefined;
  private heartbeatTimer: ReturnType<typeof setInterval> | undefined;
  /** The access token each connection was opened with (for its renewal before expiry). */
  private readonly tokens = new WeakMap<HubConnection, string>();
  private readonly statusListeners = new Set<() => void>();
  private readonly handlers = new Map<string, Set<RealtimeHandler>>();
  private readonly topics = new Map<string, number>();

  constructor(options: RealtimeManagerOptions) {
    this.options = {
      url: REALTIME_HUB_URL,
      heartbeatIntervalMs: HEARTBEAT_INTERVAL_MS,
      ...options,
    };
  }

  // ---- status -------------------------------------------------------------------------------

  getStatus = (): RealtimeStatus => this.status;

  /** For `useSyncExternalStore`. */
  subscribeStatus = (listener: () => void): (() => void) => {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  };

  private setStatus(status: RealtimeStatus): void {
    if (this.status === status) return;
    this.status = status;
    for (const listener of this.statusListeners) listener();
  }

  // ---- lifecycle ----------------------------------------------------------------------------

  /** Starts (or keeps) the connection. Safe to call repeatedly. */
  start(): void {
    if (this.running) return;
    this.running = true;
    this.restartAttempt = 0;
    void this.connect();
  }

  /** Stops the connection (e.g. on logout). Handlers and topic registrations are kept. */
  async stop(): Promise<void> {
    this.running = false;
    clearTimeout(this.restartTimer);
    clearTimeout(this.renewTimer);
    this.stopHeartbeat();
    this.hasConnectedOnce = false;
    const connection = this.connection;
    this.connection = null;
    this.setStatus('idle');
    if (connection && connection.state !== HubConnectionState.Disconnected) {
      await connection.stop().catch(() => undefined);
    }
  }

  private createConnection(): HubConnection {
    let connection: HubConnection | null = null;
    const tokenFactory = async () => {
      const token = (await this.options.accessTokenFactory()) ?? '';
      if (connection) this.tokens.set(connection, token);
      return token;
    };
    const factory = this.options.connectionFactory ?? defaultConnectionFactory;
    connection = factory(this.options.url, tokenFactory, new InfiniteBackoffPolicy());
    return this.wire(connection);
  }

  private wire(connection: HubConnection): HubConnection {
    connection.on('event', (envelope: unknown) => {
      // A connection being replaced (renewal) still receives events until it is closed: only the live one dispatches.
      if (connection !== this.connection) return;
      if (!isEnvelope(envelope)) {
        console.warn('[realtime] ignored malformed event', envelope);
        return;
      }
      if (envelope.type === REALTIME_TOPIC_REVOKED) this.forgetTopic(envelope.payload);
      this.dispatch(envelope);
    });
    connection.onreconnecting(() => {
      if (connection === this.connection) {
        this.stopHeartbeat();
        this.setStatus('reconnecting');
      }
    });
    connection.onreconnected(() => {
      if (connection === this.connection) void this.onConnected();
    });
    connection.onclose(() => {
      if (!this.isActive(connection)) return;
      this.stopHeartbeat();
      this.setStatus('reconnecting');
      this.scheduleRestart();
    });
    return connection;
  }

  private async connect(): Promise<void> {
    if (!this.running) return;
    this.connection ??= this.createConnection();
    const connection = this.connection;
    if (connection.state !== HubConnectionState.Disconnected) return;
    this.setStatus(this.hasConnectedOnce || this.restartAttempt > 0 ? 'reconnecting' : 'connecting');
    try {
      await connection.start();
    } catch {
      if (!this.isActive(connection)) return;
      this.setStatus('reconnecting');
      this.scheduleRestart();
      return;
    }
    if (!this.isActive(connection)) {
      await connection.stop().catch(() => undefined);
      return;
    }
    await this.onConnected();
  }

  /** Whether `connection` is still the live one (stop() may run while a start is awaited). */
  private isActive(connection: HubConnection): boolean {
    return this.running && connection === this.connection;
  }

  private scheduleRestart(): void {
    clearTimeout(this.restartTimer);
    this.restartAttempt++;
    this.restartTimer = setTimeout(() => void this.connect(), backoffDelay(this.restartAttempt));
  }

  private async onConnected(): Promise<void> {
    const isReconnect = this.hasConnectedOnce;
    this.hasConnectedOnce = true;
    this.restartAttempt = 0;
    this.setStatus('connected');
    this.startHeartbeat();
    this.scheduleRenewal();
    await Promise.all([...this.topics.keys()].map((topic) => this.sendSubscription('Subscribe', topic)));
    if (isReconnect) this.dispatch({ type: REALTIME_RECONNECTED, payload: null });
  }

  /** Renews the live connection shortly before its token expires (see {@link RENEW_BEFORE_EXPIRY_MS}). */
  private scheduleRenewal(): void {
    clearTimeout(this.renewTimer);
    const connection = this.connection;
    const expiry = connection ? tokenExpiryMs(this.tokens.get(connection)) : null;
    if (expiry === null) return;
    const delay = expiry - Date.now() - RENEW_BEFORE_EXPIRY_MS;
    if (delay < MIN_RENEW_DELAY_MS) return; // too late to overlap: the hub closes it and the manager reconnects
    this.renewTimer = setTimeout(() => void this.renew(), delay);
  }

  /**
   * Opens a new connection (with a fresh token), subscribes it to every topic and only then makes it the live one and
   * closes the old: nothing is missed, nobody sees the user go offline, and no refetch is needed.
   */
  private async renew(): Promise<void> {
    const old = this.connection;
    if (old?.state !== HubConnectionState.Connected || !this.isActive(old)) return;
    const next = this.createConnection();
    try {
      await next.start();
    } catch {
      await next.stop().catch(() => undefined);
      return; // the old connection closes at expiry and the usual reconnect takes over
    }
    if (!this.isActive(old)) {
      await next.stop().catch(() => undefined);
      return;
    }
    await Promise.all(
      [...this.topics.keys()].map((topic) => this.invokeSubscription(next, 'Subscribe', topic)),
    );
    if (!this.isActive(old)) {
      await next.stop().catch(() => undefined);
      return;
    }
    this.connection = next;
    this.startHeartbeat();
    this.scheduleRenewal();
    await old.stop().catch(() => undefined);
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (this.connection?.state === HubConnectionState.Connected) {
        this.connection.invoke('Heartbeat').catch(() => undefined);
      }
    }, this.options.heartbeatIntervalMs);
  }

  private stopHeartbeat(): void {
    clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = undefined;
  }

  // ---- events ---------------------------------------------------------------------------------

  /**
   * Registers a handler for an event type. Supports prefix patterns: `tasks.*` (all task events) and `*`.
   * Returns an unsubscribe function.
   */
  on<TPayload = unknown>(type: string, handler: RealtimeHandler<TPayload>): () => void {
    let set = this.handlers.get(type);
    if (!set) {
      set = new Set();
      this.handlers.set(type, set);
    }
    const h = handler as RealtimeHandler;
    set.add(h);
    return () => {
      set.delete(h);
      if (set.size === 0) this.handlers.delete(type);
    };
  }

  /** Dispatches an envelope to matching handlers. Public so tests and local emitters can inject events. */
  dispatch(envelope: RealtimeEnvelope): void {
    const run = (set: Set<RealtimeHandler> | undefined) => {
      if (!set) return;
      for (const handler of [...set]) {
        try {
          handler(envelope.payload, envelope);
        } catch (error) {
          console.error(`[realtime] handler for "${envelope.type}" failed`, error);
        }
      }
    };
    run(this.handlers.get(envelope.type));
    for (const [pattern, set] of this.handlers) {
      if (pattern === '*' || (pattern.endsWith('.*') && envelope.type.startsWith(pattern.slice(0, -1))))
        run(set);
    }
  }

  // ---- topics ---------------------------------------------------------------------------------

  /** Subscribes to a server topic (`chat:{id}`, `task:{id}`…); ref-counted. Returns an unsubscribe function. */
  subscribe(topic: string): () => void {
    const count = this.topics.get(topic) ?? 0;
    this.topics.set(topic, count + 1);
    if (count === 0) void this.sendSubscription('Subscribe', topic);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      const current = this.topics.get(topic) ?? 0;
      if (current <= 1) {
        this.topics.delete(topic);
        void this.sendSubscription('Unsubscribe', topic);
      } else {
        this.topics.set(topic, current - 1);
      }
    };
  }

  /** Topics with at least one active subscriber. */
  getTopics(): string[] {
    return [...this.topics.keys()];
  }

  /** The server revoked a topic: it must not be subscribed again after a reconnect (a later subscribe() starts anew). */
  private forgetTopic(payload: unknown): void {
    const topic = (payload as { topic?: unknown } | null)?.topic;
    if (typeof topic === 'string') this.topics.delete(topic);
  }

  private async sendSubscription(method: 'Subscribe' | 'Unsubscribe', topic: string): Promise<void> {
    const connection = this.connection;
    if (connection?.state !== HubConnectionState.Connected) return; // (re)sent on connect
    await this.invokeSubscription(connection, method, topic);
  }

  private async invokeSubscription(
    connection: HubConnection,
    method: 'Subscribe' | 'Unsubscribe',
    topic: string,
  ): Promise<void> {
    try {
      const allowed: unknown = await connection.invoke(method, topic);
      // The hub answers `false` for a topic the user may not follow (not a member, a deleted entity): forget it, so it
      // is not asked for again on every reconnect (a later subscribe() starts anew).
      if (method === 'Subscribe' && allowed === false) this.topics.delete(topic);
    } catch (error) {
      console.warn(`[realtime] ${method}("${topic}") failed`, error);
    }
  }
}
